/**
 * Query key factory for consistent cache management
 * Follows TanStack Query best practices for hierarchical key structure
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createApiKey, CreateApiKeyRequest } from '~/functions/keys/createApiKey'
import { updateApiKey, UpdateApiKeyRequest } from '~/functions/keys/updateApiKey'
import { deleteApiKey, DeleteApiKeyRequest } from '~/functions/keys/deleteApiKey'
import { getApiKeys, GetApiKeysRequest } from '~/functions/keys/getApiKeys'
import { QueryOptions } from './types'
// Base key definitions
const baseKeys = {
  orgs: ['orgs'] as const,
  users: ['users'] as const,
  admin: ['admin'] as const,
} as const

export const queryKeys = {
  // Organizations
  orgs: {
    all: baseKeys.orgs,
    lists: () => [...baseKeys.orgs, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...baseKeys.orgs, 'list', { filters }] as const,
    details: () => [...baseKeys.orgs, 'detail'] as const,
    detail: (id: string) => [...baseKeys.orgs, 'detail', id] as const,
  },

  // Users (example for extensibility)
  users: {
    all: baseKeys.users,
    lists: () => [...baseKeys.users, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...baseKeys.users, 'list', { filters }] as const,
    details: () => [...baseKeys.users, 'detail'] as const,
    detail: (id: string) => [...baseKeys.users, 'detail', id] as const,
  },

  // Admin specific queries
  admin: {
    all: baseKeys.admin,
    orgs: {
      all: [...baseKeys.admin, 'orgs'] as const,
      lists: () => [...baseKeys.admin, 'orgs', 'list'] as const,
      list: (filters: Record<string, unknown>) => [...baseKeys.admin, 'orgs', 'list', { filters }] as const,
      details: () => [...baseKeys.admin, 'orgs', 'detail'] as const,
      detail: (id: string) => [...baseKeys.admin, 'orgs', 'detail', id] as const,
      keys: (orgId: string) => [...baseKeys.admin, 'orgs', 'detail', orgId, 'keys'] as const,
      quotas: (orgId: string) => [...baseKeys.admin, 'orgs', 'detail', orgId, 'quotas'] as const,
    },
  },
} as const

// Query key type helpers (for TypeScript intellisense)
export type QueryKeyTuple = readonly (string | number | Record<string, unknown>)[]

// Helper function to invalidate related queries
export const getInvalidationKeys = {
  org: (orgId: string) => [
    queryKeys.orgs.detail(orgId),
    queryKeys.admin.orgs.detail(orgId),
    queryKeys.orgs.lists(),
    queryKeys.admin.orgs.lists(),
  ],
  orgs: () => [
    queryKeys.orgs.all,
    queryKeys.admin.orgs.all,
  ],
} as const 


// Query key generators
export const keyQueryKeys = {
  all: (orgId: string) => queryKeys.admin.orgs.keys(orgId),
  list: (orgId: string, params: Record<string, unknown>) => [...queryKeys.admin.orgs.keys(orgId), params],
}

// Get API keys hook
export function useApiKeys(
  organizationId: string | null | undefined,
  params: Omit<GetApiKeysRequest, 'organization_id'> = {},
  options: QueryOptions = {}
) {
  return useQuery({
    queryKey: keyQueryKeys.list(organizationId!, params),
    queryFn: () => getApiKeys({ data: { ...params, organization_id: organizationId! } }),
    enabled: Boolean(organizationId) && (options.enabled ?? true),
    staleTime: options.staleTime ?? 5 * 60 * 1000,
    gcTime: options.gcTime ?? 10 * 60 * 1000,
    refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
  })
}

// Create API key mutation
export function useCreateApiKey() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: CreateApiKeyRequest) => createApiKey({ data }),
    onSuccess: (_, variables) => {
      // Invalidate specific organization and its API keys
      queryClient.invalidateQueries({ queryKey: keyQueryKeys.all(variables.organization_id) })
    },
  })
}

// Update API key mutation
export function useUpdateApiKey() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: UpdateApiKeyRequest) => updateApiKey({ data }),
    onSuccess: () => {
      // Invalidate all API key queries since we don't know the organization ID
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.orgs.all })
    },
  })
}



// Delete API key mutation
export function useDeleteApiKey() {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: (data: DeleteApiKeyRequest) => deleteApiKey({ data }),
    onSuccess: () => {
      // Invalidate all API key queries since we don't know the organization ID
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.orgs.all })
    },
  })
} 
